import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductTabs = ({ product }) => {
  const [activeTab, setActiveTab] = useState('specifications');

  const tabs = [
    { id: 'specifications', label: 'Technical Specifications', icon: 'Settings' },
    { id: 'clinical', label: 'Clinical Studies', icon: 'FileText' },
    { id: 'manuals', label: 'User Manuals', icon: 'BookOpen' },
    { id: 'related', label: 'Related Products', icon: 'Package' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'specifications':
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {product?.specifications?.map((section, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-text-primary mb-3">{section?.category}</h4>
                  <div className="space-y-2">
                    {section?.items?.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex justify-between items-start py-1">
                        <span className="text-sm text-text-secondary">{item?.label}:</span>
                        <span className="text-sm font-medium text-text-primary text-right ml-4">
                          {item?.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'clinical':
        return (
          <div className="space-y-6">
            {product?.clinicalStudies?.map((study, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">{study?.title}</h4>
                    <div className="flex items-center gap-4 text-sm text-text-secondary">
                      <span>{study?.journal}</span>
                      <span>•</span>
                      <span>{study?.year}</span>
                      <span>•</span>
                      <span>{study?.participants} participants</span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
                    View Study
                  </Button>
                </div>
                <p className="text-text-secondary mb-4">{study?.summary}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {study?.keyFindings?.map((finding, findingIndex) => (
                    <div key={findingIndex} className="bg-success/5 border border-success/20 rounded-lg p-3">
                      <div className="text-lg font-bold text-success mb-1">{finding?.value}</div>
                      <div className="text-sm text-text-secondary">{finding?.metric}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case 'manuals':
        return (
          <div className="space-y-4">
            {product?.manuals?.map((manual, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={manual?.type === 'pdf' ? 'FileText' : 'Video'} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text-primary">{manual?.title}</h4>
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <span>{manual?.type?.toUpperCase()}</span>
                      <span>•</span>
                      <span>{manual?.size}</span>
                      <span>•</span>
                      <span>Updated {manual?.lastUpdated}</span>
                    </div>
                  </div>
                </div>
                <Button variant="outline" iconName="Download" iconPosition="left">
                  Download
                </Button>
              </div>
            ))}
          </div>
        );

      case 'related':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product?.relatedProducts?.map((relatedProduct, index) => (
              <div key={index} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-medical-hover transition-shadow duration-200">
                <div className="aspect-video bg-muted">
                  <img
                    src={relatedProduct?.image}
                    alt={relatedProduct?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-text-primary mb-2">{relatedProduct?.name}</h4>
                  <p className="text-sm text-text-secondary mb-3">{relatedProduct?.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-primary">
                      {new Intl.NumberFormat('en-MY', {
                        style: 'currency',
                        currency: 'MYR',
                        minimumFractionDigits: 0
                      })?.format(relatedProduct?.price)}
                    </span>
                    <Button variant="outline" size="sm">
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors duration-200 ${
                activeTab === tab?.id
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-text-secondary hover:text-text-primary hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProductTabs;